import ExcelJS from 'exceljs';
import moment from 'moment';

// Excel twin of the "Scope - Time Sheet" PDF (report 81). It is fed the same
// payload as the PDF - the schedule list from /Schedules/get-schedules-by-person -
// and prints one blank sheet per assignment, so a person, a site or a whole
// district can be handed out as a single workbook.
//
// The previous wiring pointed report 81 at ScopeTimesheetWithoutNotesExcel, which
// expects the aggregate `{ scopeTimesheetData: [...] }` shape from
// /Report/generate-report and therefore threw on every download.

const formatTime = (time?: string | null) => {
    if (!time) return '';
    const m = moment(time, ['HH:mm:ss', 'HH:mm'], true);
    return m.isValid() ? m.format('h:mm A') : '';
};

const thinBorder: Partial<ExcelJS.Borders> = {
    top: { style: 'thin' },
    left: { style: 'thin' },
    bottom: { style: 'thin' },
    right: { style: 'thin' }
};

const label = (worksheet: ExcelJS.Worksheet, row: number, col: number, text: string) => {
    const cell = worksheet.getCell(row, col);
    cell.value = text;
    cell.font = { bold: true, size: 9 };
    return cell;
};

const value = (worksheet: ExcelJS.Worksheet, row: number, colStart: number, colEnd: number, text: string) => {
    worksheet.mergeCells(row, colStart, row, colEnd);
    const cell = worksheet.getCell(row, colStart);
    cell.value = text;
    cell.font = { size: 9 };
    return cell;
};

const gridCell = (worksheet: ExcelJS.Worksheet, row: number, col: number, text: string, bold = false) => {
    const cell = worksheet.getCell(row, col);
    cell.value = text;
    cell.font = { size: 9, bold };
    cell.border = thinBorder;
    cell.alignment = { horizontal: bold ? 'center' : 'left', vertical: 'middle', wrapText: true };
    return cell;
};

const mergedGridCell = (
    worksheet: ExcelJS.Worksheet,
    row: number,
    colStart: number,
    colEnd: number,
    text: string,
    bold = false
) => {
    worksheet.mergeCells(row, colStart, row, colEnd);
    const cell = gridCell(worksheet, row, colStart, text, bold);
    // Merged cells only carry the border of their top-left cell, so paint the rest.
    for (let col = colStart + 1; col <= colEnd; col++) {
        worksheet.getCell(row, col).border = thinBorder;
    }
    return cell;
};

// Max hours are held per session on the person, so the sheet shows the one that
// matches the assignment - same rule as the PDF.
const resolveMaxHours = (schedule: any) => {
    const siteType = (schedule?.siteType || '').toLowerCase();
    if (siteType.includes('before')) return schedule?.personel?.maX_HRS_B || 0;
    if (siteType.includes('during')) return schedule?.personel?.maX_HRS_D || 0;
    if (siteType.includes('after')) return schedule?.personel?.maX_HRS_A || 0;
    return 0;
};

const renderSchedule = (worksheet: ExcelJS.Worksheet, schedule: any, startRow: number) => {
    let row = startRow;

    worksheet.mergeCells(row, 1, row, 14);
    const title = worksheet.getCell(row, 1);
    title.value = 'SCOPE Employee - Time Sheet';
    title.font = { size: 14, bold: true };
    row += 2;

    const name = `${schedule?.personel?.lastname || ''}, ${schedule?.personel?.firstname || ''}`;
    label(worksheet, row, 1, 'Name:');
    value(worksheet, row, 2, 7, name);
    label(worksheet, row, 9, 'Position:');
    value(worksheet, row, 10, 14, schedule?.position || '');
    row++;

    label(worksheet, row, 1, 'District:');
    value(worksheet, row, 2, 7, schedule?.distName || '');
    label(worksheet, row, 9, 'Budget Code:');
    value(worksheet, row, 10, 14, schedule?.distNumber != null ? String(schedule.distNumber) : '');
    row++;

    label(worksheet, row, 1, 'Max Hours:');
    value(worksheet, row, 2, 7, `${resolveMaxHours(schedule)} a week`);
    label(worksheet, row, 9, 'Program:');
    value(worksheet, row, 10, 14, schedule?.siteName || '');
    row++;

    const weekSchedule =
        `Mon ${formatTime(schedule?.mondayTimeIn)}-${formatTime(schedule?.mondayTimeOut)} | ` +
        `Tue ${formatTime(schedule?.tuesdayTimeIn)}-${formatTime(schedule?.tuesdayTimeOut)} | ` +
        `Wed ${formatTime(schedule?.wednesdayTimeIn)}-${formatTime(schedule?.wednesdayTimeOut)} | ` +
        `Thu ${formatTime(schedule?.thursdayTimeIn)}-${formatTime(schedule?.thursdayTimeOut)} | ` +
        `Fri ${formatTime(schedule?.fridayTimeIn)}-${formatTime(schedule?.fridayTimeOut)}`;
    label(worksheet, row, 1, 'Schedule:');
    value(worksheet, row, 2, 14, weekSchedule);
    row++;

    label(worksheet, row, 1, 'Comment:');
    value(worksheet, row, 2, 14, '');
    row++;

    label(worksheet, row, 1, 'IMPORTANT NOTE:');
    value(
        worksheet,
        row,
        2,
        14,
        'You may be assigned additional hours or a location change which you must accomodate in order to meet legally required staff to student ratio.'
    );
    row += 2;

    // ---------- Program hours / additional hours grids ----------
    const sectionTitle = (colStart: number, colEnd: number, text: string, size: number, bold: boolean) => {
        worksheet.mergeCells(row, colStart, row, colEnd);
        const cell = worksheet.getCell(row, colStart);
        cell.value = text;
        cell.font = { size, bold };
        cell.alignment = { horizontal: 'center' };
    };

    sectionTitle(1, 7, 'Program Hours', 12, true);
    sectionTitle(9, 14, 'Additional Hours / Extra Pay', 12, true);
    row++;
    sectionTitle(1, 7, '(Record Actual time worked)', 9, false);
    sectionTitle(9, 14, '(Record Actual time worked)', 9, false);
    row++;

    const programHead = ['', 'Date', 'In', 'Out', 'Lunch Out', 'Lunch In', '#Hours'];
    const additionalHead = ['Date', 'Start', 'Stop', 'Explanation / Location', 'Code', '#Hours'];
    programHead.forEach((text, index) => gridCell(worksheet, row, index + 1, text, true));
    additionalHead.forEach((text, index) => gridCell(worksheet, row, index + 9, text, true));
    row++;

    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
    days.forEach(day => {
        gridCell(worksheet, row, 1, day);
        for (let col = 2; col <= 7; col++) gridCell(worksheet, row, col, '');
        for (let col = 9; col <= 14; col++) gridCell(worksheet, row, col, '');
        row++;
    });

    value(worksheet, row, 1, 7, 'Total: ____________________');
    value(worksheet, row, 9, 14, 'Total: ____________________');
    row += 2;

    // ---------- "Time not at work" block and the code legend ----------
    const notAtWorkRows = [
        ['Sick (Personal illness or Immediate Family)', 'Jury Duty'],
        ['Personal Business', 'Bereavement (Indicate Relationship)'],
        ['School Closed (as Indicated on Calender)', 'Emergency Closing (Snow Days, other authorized emergencies)']
    ];
    const codeRows = [
        ['1', 'Assigned Role of Acting Director'],
        ['2', 'Assigned to another program site; Permanent sub assigned to a program outside of cluster'],
        ['3', 'Group Leader assigned to role of Small Group Leader'],
        ['4', 'Group Leader assigned to role of Head of Group']
    ];

    const blockStart = row;
    mergedGridCell(worksheet, blockStart, 1, 7, 'For documenting time not at work indicate', true);
    notAtWorkRows.forEach((pair, index) => {
        mergedGridCell(worksheet, blockStart + 1 + index, 1, 4, pair[0]);
        mergedGridCell(worksheet, blockStart + 1 + index, 5, 7, pair[1]);
    });

    gridCell(worksheet, blockStart, 9, 'Code', true);
    mergedGridCell(worksheet, blockStart, 10, 14, 'Explanation / Location', true);
    codeRows.forEach((pair, index) => {
        gridCell(worksheet, blockStart + 1 + index, 9, pair[0]);
        mergedGridCell(worksheet, blockStart + 1 + index, 10, 14, pair[1]);
    });

    row = blockStart + 1 + Math.max(notAtWorkRows.length, codeRows.length) + 1;

    // ---------- Totals, certification, signatures, office box ----------
    const footerStart = row;
    value(worksheet, row, 1, 2, 'Total Absences: ________');
    value(worksheet, row, 3, 4, 'Total Lateness: ________');
    value(worksheet, row, 5, 7, 'Total Left Early: ________');
    row += 2;

    value(
        worksheet,
        row,
        1,
        7,
        'I certify that the above account is true and correct and that the services performed were rendered to SCOPE on the dates and hours stated.'
    );
    row += 2;

    value(worksheet, row, 1, 4, 'Employee Signature: ____________________');
    value(worksheet, row, 5, 7, 'Date: ____________');
    row++;
    value(worksheet, row, 1, 4, 'Verification Signature: ____________________');
    value(worksheet, row, 5, 7, 'Date: ____________');
    row++;

    const officeRows = ['Total Hours Worked:', 'Additional Pay Due:', 'Approved by:', 'Notes:'];
    worksheet.mergeCells(footerStart, 9, footerStart + officeRows.length - 1, 10);
    const officeLabel = worksheet.getCell(footerStart, 9);
    officeLabel.value = 'Scope Office Use Only';
    officeLabel.font = { size: 9, bold: true };
    officeLabel.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    officeLabel.border = thinBorder;
    worksheet.getCell(footerStart, 10).border = thinBorder;
    officeRows.forEach((text, index) => {
        gridCell(worksheet, footerStart + index, 11, text);
        mergedGridCell(worksheet, footerStart + index, 12, 14, '');
    });

    return Math.max(row, footerStart + officeRows.length);
};

const ScopeEmployeeBlankTimeSheetExcel = async (reportData: any) => {
    // Matches the PDF: a single schedule when a site narrows it to one assignment,
    // otherwise every assignment returned for the chosen person/site/district.
    const schedules = (Array.isArray(reportData) ? reportData : [reportData]).filter(Boolean);

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Employee Timesheets');

    worksheet.columns = [
        { width: 16 }, // A - day / labels
        { width: 14 }, // B - Date
        { width: 12 }, // C - In
        { width: 12 }, // D - Out
        { width: 14 }, // E - Lunch Out
        { width: 14 }, // F - Lunch In
        { width: 12 }, // G - #Hours
        { width: 3 },  // H - gap between the two grids
        { width: 14 }, // I - Date
        { width: 12 }, // J - Start
        { width: 12 }, // K - Stop
        { width: 30 }, // L - Explanation / Location
        { width: 8 },  // M - Code
        { width: 12 }  // N - #Hours
    ];
    worksheet.pageSetup = { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 };

    let row = 1;
    schedules.forEach((schedule: any, index: number) => {
        if (index > 0) {
            // One assignment per printed page, matching the PDF.
            worksheet.getRow(row).addPageBreak();
        }
        row = renderSchedule(worksheet, schedule, row) + 3;
    });

    return await workbook.xlsx.writeBuffer();
};

export default ScopeEmployeeBlankTimeSheetExcel;
